import * as React from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SortController } from '@/lib/sort';

interface SortableThProps<K extends string> extends Omit<React.ThHTMLAttributes<HTMLTableCellElement>, 'children'> {
  sortKey: K;
  controller: SortController<K>;
  align?: 'left' | 'right' | 'center';
  children: React.ReactNode;
}

/**
 * A table header cell that sorts its column when clicked. It keeps whatever
 * classes the table already gives its headers; only the inner button and the
 * direction indicator are added.
 */
export function SortableTh<K extends string>({
  sortKey,
  controller,
  align = 'left',
  className,
  children,
  ...rest
}: SortableThProps<K>): React.ReactElement {
  const active = controller.sort?.key === sortKey;
  const direction = active ? controller.sort?.direction : undefined;
  const Icon = direction === 'asc' ? ArrowUp : direction === 'desc' ? ArrowDown : ChevronsUpDown;
  return (
    <th
      {...rest}
      aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}
      className={cn(className, align === 'right' && 'text-right', align === 'center' && 'text-center')}
    >
      <button
        type="button"
        onClick={() => controller.toggle(sortKey)}
        title={`Sort by ${typeof children === 'string' ? children.toLowerCase() : 'this column'}`}
        className={cn(
          'group/sort inline-flex max-w-full items-center gap-1 rounded-sm font-[inherit] text-[inherit] tracking-[inherit] [text-transform:inherit] transition-colors hover:text-[hsl(var(--foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]',
          align === 'right' && 'flex-row-reverse',
          active && 'text-[hsl(var(--foreground))]',
        )}
      >
        <span className="truncate">{children}</span>
        <Icon
          aria-hidden
          className={cn(
            'size-3.5 shrink-0 transition-opacity',
            active ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-60',
          )}
        />
      </button>
    </th>
  );
}
